import React, { useState, useEffect, useCallback } from 'react';
import AddMembersModal from './AddMembersModal';
import axiosInstance from '../api/axiosInstance';
import Card, { CardHeader } from '../ui/Card';

// Icons
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  EnvelopeIcon,
  DocumentDuplicateIcon,
  LockClosedIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import {
  ArrowPathIcon,
  UserGroupIcon as UserGroupIconSolid,
  EnvelopeIcon as EnvelopeIconSolid
} from '@heroicons/react/24/solid';
import logger from '../../utils/logger';

const TeamDetailsModal = ({ open, onClose, team: initialTeam, onEdit, onDelete, onUpdateMembers, showNotification }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [updatedSettings, setUpdatedSettings] = useState(initialTeam?.settings || {});
  const [team, setTeam] = useState(initialTeam || { members: [] });
  const [loading, setLoading] = useState(false);

  const fetchTeamDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/business/teams/${initialTeam.id}`);

      if (response.data.success) {

        setTeam(response.data.team);
        setUpdatedSettings(response.data.team.settings || {});
      }
    } catch (error) {
      logger.error('Error fetching team details:', error);
      showNotification?.('Failed to load team details', 'error');
    } finally {
      setLoading(false);
    }
  }, [initialTeam?.id]);

  useEffect(() => {
    if (open && initialTeam?.id) {
      fetchTeamDetails();
    }
  }, [open, initialTeam?.id, fetchTeamDetails]);

  const handleRemoveMember = async (userId) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      try {
        const updatedMembers = team.members.filter(member => member.userId !== userId);
        await onUpdateMembers({ members: updatedMembers });
        await fetchTeamDetails();
        showNotification?.('Member removed successfully', 'success');
      } catch (error) {
        logger.error('Error removing member:', error);
        showNotification?.('Failed to remove member', 'error');
      }
    }
  };

  const handleUpdateSettings = async (setting, value) => {
    try {
      const newSettings = {
        ...updatedSettings,
        [setting]: value
      };
      setUpdatedSettings(newSettings);

      if (['allowExternalSenders', 'moderationEnabled'].includes(setting)) {
        await onUpdateMembers({
          distributionSettings: {
            ...team.distributionSettings,
            [setting]: value
          }
        });
      } else {
        await onUpdateMembers({
          settings: newSettings
        });
      }

      showNotification?.('Settings updated successfully', 'success');
    } catch (error) {
      logger.error('Error updating settings:', error);
      showNotification?.('Failed to update settings', 'error');
      setUpdatedSettings(team.settings);
    }
  };

  const handleAddMembers = async (newMembers) => {
    try {
      const existingUserIds = new Set((team?.members || []).map(m => m.userId));
      const uniqueNewMembers = newMembers.filter(member => !existingUserIds.has(member.userId));

      const updatedMembers = [
        ...(team?.members || []),
        ...uniqueNewMembers
      ];

      await onUpdateMembers({ members: updatedMembers });
      setShowAddMembersModal(false);
      await fetchTeamDetails();
      showNotification?.('Members added successfully', 'success');
    } catch (error) {
      logger.error('Error adding members:', error);
      showNotification?.('Failed to add members', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification?.('Copied to clipboard!', 'success');
  };

  useEffect(() => {
    setUpdatedSettings(team?.settings || {});
  }, [team]);

  const tabs = [
    { id: 'general', label: 'General', icon: <InformationCircleIcon className="w-4 h-4" /> },
    { id: 'members', label: 'Members', icon: <UsersIcon className="w-4 h-4" /> },
    { id: 'settings', label: 'Settings', icon: <Cog6ToothIcon className="w-4 h-4" /> }
  ];

  const renderGeneralInfo = () => (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-br from-orange-50 to-blue-50 dark:from-orange-900/20 dark:to-blue-900/20 border-orange-200 dark:border-orange-800">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              {team.type === 'distribution_list' ? (
                <EnvelopeIconSolid className="w-8 h-8 text-white" />
              ) : (
                <UserGroupIconSolid className="w-8 h-8 text-white" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{team.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {team.description || 'No description provided'}
              </p>
              <div className="flex items-center space-x-2 mt-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  team.type === 'distribution_list'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                }`}>
                  {team.type === 'distribution_list' ? 'Distribution List' : 'Team'}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  team.privacy === 'private'
                    ? 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                }`}>
                  {team.privacy === 'private' ? (
                    <><LockClosedIcon className="w-3 h-3 mr-1" /> Private</>
                  ) : (
                    <><GlobeAltIcon className="w-3 h-3 mr-1" /> Public</>
                  )}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => onEdit()}
            className="p-2 rounded-lg text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
            title="Edit team"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
        </div>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email Address (for distribution lists) */}
        {team.type === 'distribution_list' && team.email && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <EnvelopeIcon className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 dark:text-white">Email Address</h4>
              </div>
            </CardHeader>
            <div className="flex items-center justify-between mt-2">
              <code className="text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#2a2a2a] px-3 py-1.5 rounded">
                {team.email}
              </code>
              <button
                onClick={() => copyToClipboard(team.email)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
                title="Copy email"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            </div>
          </Card>
        )}

        {/* Member Count */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Members</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {team?.members?.length || 0}
              </p>
            </div>
          </div>
        </Card>

        {/* Created Date */}
        <Card>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {new Date(team.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </Card>

        {/* Last Updated */}
        {team.updatedAt && (
          <Card>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {new Date(team.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );

  const renderMembers = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Team Members
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {(team?.members || []).length} {(team?.members || []).length === 1 ? 'member' : 'members'}
          </p>
        </div>
        <button
          onClick={() => setShowAddMembersModal(true)}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 transition-all shadow-sm hover:shadow-md text-sm font-medium"
        >
          <UserPlusIcon className="w-4 h-4 mr-2" />
          Add Members
        </button>
      </div>

      <Card>
        {!team?.members || team.members.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No members yet
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Get started by adding members to this team
            </p>
            <button
              onClick={() => setShowAddMembersModal(true)}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors text-sm font-medium"
            >
              <UserPlusIcon className="w-4 h-4 mr-2" />
              Add First Member
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {team.members.map((member) => {
              // Handle both direct member properties and nested userId structure
              const user = member.userId || member;
              const firstName = user.firstName || member.firstName || '';
              const lastName = user.lastName || member.lastName || '';
              const email = user.email || member.email || '';
              const department = user.department || member.department || '';
              const memberId = member._id || member.userId?._id || member.userId;

              return (
                <div
                  key={memberId}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white flex items-center justify-center font-semibold text-lg">
                      {firstName?.[0]?.toUpperCase() || ''}
                      {lastName?.[0]?.toUpperCase() || ''}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {`${firstName} ${lastName}`.trim() || 'Unnamed User'}
                        </p>
                        {member.role === 'owner' && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 font-semibold">
                            Owner
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{email}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'admin'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300'
                        }`}>
                          {member.role || 'member'}
                        </span>
                        {department && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">
                            {department}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMember(memberId)}
                    disabled={member.role === 'owner'}
                    className={`ml-4 p-2 rounded-lg transition-colors ${
                      member.role === 'owner'
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                    title={member.role === 'owner' ? 'Cannot remove owner' : 'Remove member'}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      {/* Team Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Cog6ToothIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Team Settings</h4>
          </div>
        </CardHeader>

        <div className="space-y-4 mt-4">
          {[
            {
              key: 'allowMessaging',
              label: 'Allow Messaging',
              description: 'Members can send messages in team channels'
            },
            {
              key: 'allowFileSharing',
              label: 'Allow File Sharing',
              description: 'Members can upload and share files'
            }
          ].map((setting) => (
            <div key={setting.key} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 dark:text-white">{setting.label}</h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{setting.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={team.settings?.[setting.key]}
                  onChange={(e) => handleUpdateSettings(setting.key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-[#2a2a2a] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-orange-600"></div>
              </label>
            </div>
          ))}
        </div>
      </Card>

      {/* Distribution List Settings */}
      {team.type === 'distribution_list' && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <EnvelopeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-semibold text-gray-900 dark:text-white">Distribution List Settings</h4>
            </div>
          </CardHeader>

          <div className="space-y-4 mt-4">
            {[
              {
                key: 'allowExternalSenders',
                label: 'Allow External Senders',
                description: 'Allow emails from addresses outside your organization'
              },
              {
                key: 'moderationEnabled',
                label: 'Enable Moderation',
                description: 'All messages require approval before distribution'
              }
            ].map((setting) => (
              <div key={setting.key} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 dark:text-white">{setting.label}</h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{setting.description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={team.distributionSettings?.[setting.key]}
                    onChange={(e) => handleUpdateSettings(setting.key, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#2a2a2a] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                {team.type === 'distribution_list' ? (
                  <EnvelopeIconSolid className="w-5 h-5 text-white" />
                ) : (
                  <UserGroupIconSolid className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {team.name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Team Details
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111111]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-[#1a1a1a]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto" style={{maxHeight: 'calc(90vh - 180px)'}}>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ArrowPathIcon className="h-12 w-12 text-orange-500 animate-spin mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Loading team details...</p>
              </div>
            ) : (
              <>
                {activeTab === 'general' && renderGeneralInfo()}
                {activeTab === 'members' && renderMembers()}
                {activeTab === 'settings' && renderSettings()}
              </>
            )}
          </div>
        </div>
      </div>

      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        team={team}
        existingMembers={team?.members}
        onSubmit={handleAddMembers}
        showNotification={showNotification}
      />
    </>
  );
};

export default TeamDetailsModal;
